import { observer, use$ } from '@legendapp/state/react';
import { SAMPLE, C, satMeta, momMeta, platformMeta } from '../data/sample';
import type { SortKey } from '../state';
import { state$, openNiche } from '../state';

const SORT_OPTIONS: { k: SortKey; l: string }[] = [
  { k: 'saturation', l: 'Saturation' },
  { k: 'momentum', l: 'Momentum' },
  { k: 'apps', l: 'Apps' },
  { k: 'rating', l: 'Rating' },
];

export const Explorer = observer(function Explorer() {
  const search = use$(state$.search);
  const sk = use$(state$.sortKey);
  const platformSlug = use$(state$.platformSlug);
  const d = SAMPLE;
  const platform = platformMeta(platformSlug);

  const q = search.trim().toLowerCase();
  let list = d.niches.filter(
    (n) => n.platform === platformSlug && (!q || n.name.toLowerCase().includes(q)),
  );
  list = [...list].sort((a, b) => {
    if (sk === 'saturation') return b.sat - a.sat;
    if (sk === 'apps') return b.appsReal - a.appsReal;
    if (sk === 'rating') return b.rating - a.rating;
    if (sk === 'momentum') {
      const r = { rising: 2, flat: 1, falling: 0 } as const;
      return r[b.mom] - r[a.mom];
    }
    return 0;
  });

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', animation: 'nl-fade .3s ease' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 20,
          marginBottom: 6,
        }}
      >
        <div>
          <h1
            style={{ fontSize: 27, fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 5px' }}
          >
            Explore niches on {platform.name}
          </h1>
          <p style={{ margin: 0, color: '#616161', fontSize: 14.5, maxWidth: 560 }}>
            Each niche ships with a pre-computed saturation score and a path to evidence. Pinpoint
            the open spots in seconds.
          </p>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div
            style={{
              fontFamily: "'JetBrains Mono'",
              fontSize: 30,
              fontWeight: 700,
              letterSpacing: '-0.02em',
            }}
          >
            {list.length}
          </div>
          <div
            style={{
              fontSize: 12,
              color: '#6d7175',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            niches indexed
          </div>
        </div>
      </div>

      {/* sort bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          margin: '20px 0 14px',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 12.5, color: '#6d7175', fontWeight: 600, marginRight: 2 }}>
          Sort:
        </span>
        {SORT_OPTIONS.map((o) => {
          const on = sk === o.k;
          return (
            <button
              key={o.k}
              onClick={() => state$.sortKey.set(o.k)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                height: 32,
                padding: '0 12px',
                border: `1px solid ${on ? C.brand : '#e1e3e5'}`,
                background: on ? C.brand : '#fff',
                borderRadius: 8,
                fontFamily: 'inherit',
                fontSize: 12.5,
                fontWeight: 600,
                color: on ? '#fff' : C.ink2,
                cursor: 'pointer',
              }}
            >
              {o.l}{' '}
              <span style={{ fontFamily: "'JetBrains Mono'", opacity: 0.7 }}>{on ? '↓' : ''}</span>
            </button>
          );
        })}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))',
          gap: 14,
        }}
      >
        {list.map((n) => {
          const sm = satMeta(n.sat);
          const mm = momMeta(n.mom);
          return (
            <div
              key={n.id}
              onClick={() => openNiche(n.id)}
              style={{
                background: '#fff',
                border: '1px solid #e1e3e5',
                borderRadius: 14,
                padding: 18,
                cursor: 'pointer',
                transition: 'box-shadow .18s,border-color .18s,transform .18s',
                animation: 'nl-rise .35s ease',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 10,
                  marginBottom: 14,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 9,
                      background: n.iconBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 17,
                      flexShrink: 0,
                    }}
                  >
                    {n.icon}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 15,
                        letterSpacing: '-0.01em',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {n.name}
                    </div>
                    <div
                      style={{ fontSize: 11.5, color: '#9aa0ab', fontFamily: "'JetBrains Mono'" }}
                    >
                      {n.appsReal} apps
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '3px 8px',
                    borderRadius: 7,
                    background: n.ready ? '#ebf3fb' : '#ebebeb',
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: n.ready ? '#2c6ecb' : C.ink3,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {n.ready ? '● Ready' : '◷ Not analyzed'}
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'space-between',
                  marginBottom: 7,
                }}
              >
                <span style={{ fontSize: 12, color: '#6d7175', fontWeight: 600 }}>Saturation</span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono'",
                    fontSize: 13,
                    fontWeight: 700,
                    color: sm.fg,
                  }}
                >
                  {n.sat} · {sm.label}
                </span>
              </div>
              <div
                style={{
                  height: 8,
                  borderRadius: 6,
                  background: '#ebebeb',
                  overflow: 'hidden',
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${n.sat}%`,
                    borderRadius: 6,
                    background: sm.bar,
                    transformOrigin: 'left',
                    animation: 'nl-grow .5s ease',
                  }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 12.5 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    color: mm.fg,
                    fontWeight: 600,
                  }}
                >
                  <span style={{ fontFamily: "'JetBrains Mono'", fontSize: 14 }}>{mm.arrow}</span>{' '}
                  {mm.label}
                </div>
                <div style={{ width: 1, height: 13, background: '#e1e3e5' }} />
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    color: '#616161',
                    fontWeight: 600,
                  }}
                >
                  ★ {n.rating.toFixed(2)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});
